import { Head } from "@inertiajs/react"
import { Badge, Card, Flex, Heading, Text } from "@radix-ui/themes"
import { CheckCircle, Users, Clock } from "lucide-react"
import { z } from "zod"

import ImportPreviewTable from "@/components/import-preview-table"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectCustomersPath, projectImportsPath } from "@/routes"
import { ImportSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  import: ImportSchema,
  headers: z.array(z.string()),
  preview_data: z.array(z.array(z.string().nullable())),
  customers_imported: z.number().optional(),
})

type Props = z.infer<typeof PropsSchema>

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getStatusColor(status: string) {
  switch (status) {
    case "pending":
      return "yellow"
    case "ready_to_process":
      return "blue"
    case "processing":
      return "orange"
    case "completed":
      return "green"
    case "failed":
      return "red"
    default:
      return "gray"
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case "ready_to_process":
      return "Ready to process"
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

export default function ShowImport(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, import: importData, headers, preview_data, customers_imported } = validatedProps

  const isCompleted = importData.status === "completed"
  const isFailed = importData.status === "failed"

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectImportsPath(project.slug) },
        { title: "Imports", href: projectImportsPath(project.slug) },
        { title: isCompleted ? "Import completed" : "Import details" },
      ]}
    >
      <Head title={isCompleted ? "Import completed" : "Import details"} />

      <Flex direction="column" gap="6" p="6">
        <div>
          <Flex align="center" gap="3" mb="4">
            {isCompleted && <CheckCircle className="h-6 w-6 text-green-600" />}
            <Heading size="6" weight="medium">
              {isCompleted ? "Import completed successfully!" : "Import details"}
            </Heading>
          </Flex>

          {isCompleted && (
            <Text size="3" color="green" mb="6">
              Your CSV file has been successfully imported and customers have been added to your
              project.
            </Text>
          )}

          {isFailed && (
            <Text size="3" color="red" mb="6">
              Import failed. Please check your CSV file and try again.
            </Text>
          )}
        </div>

        <Card className="w-96">
          <Flex direction="column" gap="4" p="4">
            <Flex justify="between" align="center">
              <Text size="2" weight="bold" color="gray">
                Import status
              </Text>
              <Badge color={getStatusColor(importData.status)}>
                {getStatusLabel(importData.status)}
              </Badge>
            </Flex>

            <Flex justify="between" align="center">
              <Text size="2" weight="bold" color="gray">
                File size
              </Text>
              <Text size="2">
                {importData.file_count_lines
                  ? `${importData.file_count_lines.toLocaleString()} lines`
                  : "-"}
              </Text>
            </Flex>

            {importData.email_column_index !== null && (
              <Flex justify="between" align="center">
                <Text size="2" weight="bold" color="gray">
                  Email column
                </Text>
                <Text size="2">Column {importData.email_column_index}</Text>
              </Flex>
            )}

            <Flex justify="between" align="center">
              <Text size="2" weight="bold" color="gray">
                Created
              </Text>
              <Text size="2">{formatDate(importData.created_at)}</Text>
            </Flex>

            {importData.imported_at && (
              <Flex justify="between" align="center">
                <Text size="2" weight="bold" color="gray">
                  Completed
                </Text>
                <Text size="2">{formatDate(importData.imported_at)}</Text>
              </Flex>
            )}

            {customers_imported !== undefined && (
              <Flex justify="between" align="center">
                <Text size="2" weight="bold" color="gray">
                  Customers imported
                </Text>
                <Text size="2" color="green" weight="bold">
                  {customers_imported.toLocaleString()}
                </Text>
              </Flex>
            )}
          </Flex>
        </Card>

        {isCompleted && (
          <Flex gap="3">
            <LinkButton href={projectCustomersPath(project.slug)} variant="solid">
              <Users className="h-4 w-4" />
              <span>View customers</span>
            </LinkButton>
            <LinkButton href={projectImportsPath(project.slug)}>
              <Clock className="h-4 w-4" />
              <span>View all imports</span>
            </LinkButton>
          </Flex>
        )}

        <ImportPreviewTable
          headers={headers}
          preview_data={preview_data}
          file_count_lines={importData.file_count_lines}
        />
      </Flex>
    </ProjectLayout>
  )
}
